import { UIElement } from "./ui_element.js";
import { ColorScheme } from "./color_scheme.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { TextCounterTransform, CounterOptions, ITextDisplayable } from "../../transforms/transform_text_counter.js";
import { Rect } from "../../common/rect.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { Easing } from "../../transforms/transform_base.js";

export enum MyTextAlign
{
  Left,
  Right,
  Center
}

export enum MyTextVerticalAlign
{
  Top,
  Center,
  Bottom
}

export interface UITextGradientStop
{
  offset: number;
  color: string;
}

export interface UITextGradient
{
  direction: "vertical" | "horizontal";
  stops: UITextGradientStop[];
}

export type MyTextFontStyle = "normal" | "italic" | "oblique";

export interface UITextOptions
{
  text?:                 string;
  text_color?:           string;
  text_style?:           string;
  text_align?:           MyTextAlign;
  vertical_align?:       MyTextVerticalAlign;
  padding_y?:            number;
  outline_width?:        number;
  outline_color?:        string;
  outline_offset_x?:     number;
  outline_offset_y?:     number;
  outline_shadow_blur?:  number;
  outline_shadow_color?: string;
  outline_shadow_offset_x?: number;
  outline_shadow_offset_y?: number;
  outline_gradient_color?: UITextGradient;
  font_size?:            number;
  font_weight?:          number;
  show_dbg_zone?:        boolean;
  shadow_blur?:          number;
  shadow_color?:         string;
  shadow_offset_x?:      number;
  shadow_offset_y?:      number;
  text_variable_params?: string[];
  localization_key?:     string;
  auto_wrap?:            boolean;
  arc_radius?:           number;
  font_style?:           MyTextFontStyle;
  using_y_offset?:       boolean;
  gradient_text_color?:  UITextGradient;
  line_height?:          number;
  fit_font_size?:        boolean;
  min_font_size?:        number;
  natural_text_width?:   boolean;
  scale_to_width?:       boolean;
  letter_spacing?:       number;
  color_map?:            Record<string, string>;
}

export class UITextElement extends UIElement implements ITextDisplayable
{
  protected text:                  string;
  protected text_color:            string;
  protected text_style:            string;
  protected text_align:            MyTextAlign;
  protected outline_width:         number;
  protected outline_color:         string;
  protected outline_offset_x:      number;
  protected outline_offset_y:      number;
  protected outline_shadow_blur:   number;
  protected outline_shadow_color:  string;
  protected outline_shadow_offset_x: number;
  protected outline_shadow_offset_y: number;
  protected outline_gradient_color?: UITextGradient;
  protected font_size:             number;
  protected font_weight:           number;
  protected show_dbg_zone:         boolean;
  protected shadow_blur:           number;
  protected shadow_color:          string;
  protected shadow_offset_x:       number;
  protected shadow_offset_y:       number;
  protected vertical_align:        MyTextVerticalAlign = MyTextVerticalAlign.Center; 
  protected padding_y:             number = 0; 
  protected font_style:            MyTextFontStyle = "normal";
  protected using_y_offset:        boolean = true;
  protected gradient_text_color?:  UITextGradient;
  protected line_height:           number = 1.1;
  protected fit_font_size:         boolean = false;
  protected min_font_size:         number = 8;
  protected scale_to_width:        boolean = false;
  protected letter_spacing:        number = 0;
  protected base_font_size:        number;

  protected original_text:         string;
  protected text_variable_params:  string[] = [];

  protected localization_key?: string;
  protected onLangChange?: ( lang: string ) => void;

  protected color_map: Record<string, string> =
  {
    red:    ColorScheme.red,
    yellow: ColorScheme.difficulty_yellow,
    green:  ColorScheme.normal_green,
    blue:   ColorScheme.blue,
    cream:  ColorScheme.cream,
    pink:   '#FF06AC',
  };

  protected weight_map: Record<string, number> = 
  {
    thin:      100,
    light:     300,
    normal:    400,
    medium:    500,
    semibold:  600,
    bold:      700,
    extrabold: 800,
    black:     900
  };

  protected auto_wrap: boolean = true;
  protected arc_radius?: number = 0;
  protected natural_text_width: boolean = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: UITextOptions = {}
  )
  {
    super( ctx, x, y, width, height, false );
    const 
    {
      text:                 text                 = "NO TEXT!",
      text_color:           text_color           = ColorScheme.black,
      text_style:           text_style           = TextStyleScheme.rubik,
      text_align:           text_align           = MyTextAlign.Center,
      outline_width:        outline_width        = 0,
      outline_color:        outline_color        = ColorScheme.black,
      outline_offset_x:     outline_offset_x     = 0,
      outline_offset_y:     outline_offset_y     = 0,
      outline_shadow_blur:  outline_shadow_blur  = 0,
      outline_shadow_color: outline_shadow_color = "rgba(0, 0, 0, 0)",
      outline_shadow_offset_x: outline_shadow_offset_x = 0,
      outline_shadow_offset_y: outline_shadow_offset_y = 0,
      outline_gradient_color: outline_gradient_color,
      font_size:            font_size            = height * 0.5,
      font_weight:          font_weight          = 400,
      show_dbg_zone:        show_dbg_zone        = false,
      shadow_blur:          shadow_blur          = 0,
      shadow_color:         shadow_color         = "rgba(0, 0, 0, 0.5)",
      shadow_offset_x:      shadow_offset_x      = 2,
      shadow_offset_y:      shadow_offset_y      = 2,
      text_variable_params: text_variable_params = [],
      localization_key:     localization_key,
      auto_wrap:            auto_wrap            = true,
      arc_radius:           arc_radius,
      vertical_align:       vertical_align       = MyTextVerticalAlign.Center,
      padding_y:            padding_y,
      font_style:           font_style           = "normal",
      using_y_offset:       using_y_offset       = true,
      gradient_text_color:  gradient_text_color,
      line_height:          line_height          = 1.1,
      fit_font_size:        fit_font_size        = false,
      min_font_size:        min_font_size        = 8,
      natural_text_width:   natural_text_width   = false,
      scale_to_width:       scale_to_width       = false,
      letter_spacing:       letter_spacing       = 0,
      color_map:            color_map
    } = options;

    this.original_text        = text;
    this.text                 = text;
    this.text_color           = text_color;
    this.text_style           = text_style;
    this.text_align           = text_align;
    this.outline_width        = outline_width;
    this.outline_color        = outline_color;
    this.outline_offset_x     = outline_offset_x;
    this.outline_offset_y     = outline_offset_y;
    this.outline_shadow_blur  = outline_shadow_blur;
    this.outline_shadow_color = outline_shadow_color;
    this.outline_shadow_offset_x = outline_shadow_offset_x;
    this.outline_shadow_offset_y = outline_shadow_offset_y;
    this.outline_gradient_color = outline_gradient_color;
    this.font_size            = font_size;
    this.font_weight          = Math.max( 100, Math.min( 900, font_weight ) );
    this.show_dbg_zone        = show_dbg_zone;
    this.shadow_blur          = shadow_blur;
    this.shadow_color         = shadow_color;
    this.shadow_offset_x      = shadow_offset_x;
    this.shadow_offset_y      = shadow_offset_y;
    this.text_variable_params = text_variable_params;
    this.localization_key     = localization_key;
    this.auto_wrap            = auto_wrap;
    this.arc_radius           = arc_radius
    this.vertical_align       = vertical_align ?? MyTextVerticalAlign.Center;
    this.padding_y            = padding_y ?? 0;
    this.font_style           = font_style
    this.using_y_offset       = using_y_offset
    this.gradient_text_color  = gradient_text_color
    this.line_height          = line_height
    this.fit_font_size        = fit_font_size
    this.min_font_size        = min_font_size
    this.natural_text_width   = natural_text_width
    this.scale_to_width       = scale_to_width
    this.letter_spacing       = letter_spacing
    this.base_font_size       = font_size

    if ( color_map )
      this.color_map = { ...this.color_map, ...color_map };

    if ( this.localization_key )
    {
      this.onLangChange = () => this.updateLocalizedText();
      LocalizationManager.subscribe( this.onLangChange );
      this.updateLocalizedText();
    } else
    {
      this.original_text = text ?? "NO TEXT!";
      this.text = this.substituteParameters( this.original_text );
    }
  }

  public override cleanup(): void
  {
    if ( this.onLangChange )
    {
      LocalizationManager.unsubscribe( this.onLangChange );
      this.onLangChange = undefined;
    }
    super.cleanup();
  }

  public setLocalizationKey( key: string ): this
  {
    if ( this.onLangChange )
      LocalizationManager.unsubscribe( this.onLangChange );

    this.localization_key = key;

    this.onLangChange = () => this.updateLocalizedText();
    LocalizationManager.subscribe( this.onLangChange );

    this.updateLocalizedText();

    return this;
  }

  private updateLocalizedText(): void
  {
    const lang = LocalizationManager.getCurrentLanguage();
    let template: string = LocalizationManager
      .getInstance()
      .getText( lang, this.localization_key! );
    template = template.replace(/\\n/g, '\n');
    this.original_text = template;
    this.text = this.substituteParameters( template );
    this.forceMakeShouldBeRendered();
  }

  private createTextGradient(
    x: number,
    y: number,
    w: number,
    h: number,
    gradient: UITextGradient = this.gradient_text_color!
  ): CanvasGradient
  {
    let grad: CanvasGradient;

    if ( gradient.direction === "vertical" )
    {
      grad = this.ctx.createLinearGradient(
        x,
        y - h / 2,
        x,
        y + h / 2
      );
    } else
    {
      grad = this.ctx.createLinearGradient(
        x,
        y,
        x + w,
        y
      );
    }

    for ( const stop of gradient.stops )
    {
      grad.addColorStop( stop.offset, stop.color );
    }

    return grad;
  }

  public name(): string
  {
    return "UITextElement";
  }

  public static heightToFontSize( h: number ): number
  {
    return h * 0.5;
  }

  private substituteParameters( template: string ): string
  {
    return template.replace( /\{(\d+)\}/g, ( match, idxStr ) =>
    {
      const idx = parseInt( idxStr, 10 );
      if ( idx >= 0 && idx < this.text_variable_params.length )
      {
        return this.text_variable_params[idx];
      } else
      {
        console.warn( `No parameter provided for index ${ idx }` );
        return '';
      }
    } );
  }

  public setTextVariableParams( text_variable_params: string[] ): this
  {
    this.text_variable_params = text_variable_params;
    this.text = this.substituteParameters( this.original_text );
    return this;
  }

  public setText( text: string ): this
  {
    this.original_text = text;
    this.text = this.substituteParameters( this.original_text );
    this.forceMakeShouldBeRendered();
    return this;
  }

  public measureTextBounds(): { x: number; y: number; width: number; height: number }
  {
    const ctx = this.ctx;
    const h = this.rect.getH();
    const w = this.rect.getW();
    const w2 = this.rect.getW2();

    let lines = this.text.split( "\n" );

    if ( this.auto_wrap )
    {
      ctx.font = `${ this.font_style } ${ this.font_weight } ${ this.font_size }px ${ this.text_style }`;
      const wrapped: string[] = [];
      for ( const line of lines )
      {
        if ( ctx.measureText( line ).width > w )
        {
          const sublines = this.wrapTextToLines( line, w, this.font_size );
          wrapped.push( ...sublines );
        } else
        {
          wrapped.push( line );
        }
      }
      lines = wrapped;
    }

    const line_height_multiplier = this.line_height ?? 1.1;
    let font_size = this.font_size;
    if ( this.fit_font_size )
    {
      font_size = this.getFittedFontSize( lines, w, h, font_size, line_height_multiplier );
    }

    if ( lines.length > 1 )
    {
      const max_font_size = h / ( lines.length * line_height_multiplier );
      font_size = Math.min( font_size, max_font_size );
    }

    ctx.font = `${this.font_style} ${this.font_weight} ${font_size}px ${this.text_style}`;
    const line_height = font_size * line_height_multiplier;
    const total_text_height = lines.length * line_height;

    let start_y: number;
    switch ( this.vertical_align )
    {
      case MyTextVerticalAlign.Top:
        start_y = -h / 2 + line_height / 2 + this.padding_y;
        break;
      case MyTextVerticalAlign.Bottom:
        start_y = h / 2 - total_text_height + line_height / 2 + this.padding_y;
        break;
      default:
        start_y = -total_text_height / 2 + line_height / 2 + this.padding_y;
        break;
    }

    let max_width = 0;
    for ( const line of lines )
    {
      max_width = Math.max( max_width, ctx.measureText( line ).width );
    }

    let base_x: number;
    switch ( this.text_align )
    {
      case MyTextAlign.Left:
        base_x = -w2;
        break;
      case MyTextAlign.Right:
        base_x = w2 - max_width;
        break;
      default:
        base_x = -max_width / 2;
    }

    const world_x = this.rect.getX() + base_x + w2;
    const world_y = this.rect.getY() + start_y + h / 2;

    return {
      x: world_x,
      y: world_y,
      width: max_width,
      height: total_text_height
    };
  }

  private measureRenderedTextWidth( value: string ): number
  {
    const metrics = this.ctx.measureText( value );
    const bbox_left  = metrics.actualBoundingBoxLeft  ?? 0;
    const bbox_right = metrics.actualBoundingBoxRight ?? 0;
    const bbox_width = bbox_left + bbox_right;
    return Math.max( metrics.width, bbox_width );
  }

  public setNaturalTextWidth( fit: boolean ): this
  {
    this.natural_text_width = fit;
    return this;
  }

  public getNaturalTextWidth(): number
  {
    const raw_text = this.text ?? "";
    const lines = raw_text.length > 0 ? raw_text.split( "\n" ) : [""];
    let max_width = 0;

    for ( const line of lines )
    {
      const segments = this.parseSegments( line );
      let line_width = 0;
      for ( const seg of segments )
      {
        this.ctx.font = `${ this.font_style } ${ seg.weight } ${ this.font_size }px ${ this.text_style }`;
        line_width += this.measureRenderedTextWidth( seg.text );
      }
      max_width = Math.max( max_width, line_width );
    }

    return max_width;
  }

  public getNaturalTextWidthAtBaseFontSize(): number
  {
    const current_font_size = this.font_size;
    this.font_size = this.base_font_size;
    const width = this.getNaturalTextWidth();
    this.font_size = current_font_size;
    return width;
  }

  public setTextColor( color: string ): this
  {
    this.text_color = color;
    return this;
  }

  public setTextStyle( style: string ): this
  {
    this.text_style = style;
    return this;
  }

  public setTextAlign( align: MyTextAlign ): this
  {
    this.text_align = align;
    return this;
  }

  public setOutline( width: number, color: string, offset_x: number = 0, offset_y: number = 0 ): this
  {
    this.outline_width = width;
    this.outline_color = color;
    this.outline_offset_x = offset_x;
    this.outline_offset_y = offset_y;
    return this;
  }

  public setFontWeight( weight: number ): this
  {
    this.font_weight = Math.max( 100, Math.min( 900, weight ) );
    return this;
  }

  public setShadow( blur: number, color: string, offsetX: number = 2, offsetY: number = 2 ): this
  {
    this.shadow_blur = blur;
    this.shadow_color = color;
    this.shadow_offset_x = offsetX;
    this.shadow_offset_y = offsetY;
    return this;
  }

  public removeShadow(): this
  {
    this.shadow_blur = 0;
    return this;
  }

  public getText(): string
  {
    return this.text || "";
  }

  public animateCounter( now: number, options: CounterOptions ): this
  {
    const counterTransform = new TextCounterTransform( this, now, options );
    this.addTransform( counterTransform );
    return this;
  }

  protected updateBasic( now: number ): void
  {
    super.updateBasic( now );
  }

  public countPercentage( now: number, target_value: number, duration_ms: number = 1000 ): this
  {
    const current_num = this.extractNumberFromText() || 0;
    return this.animateCounter( now, 
    {
      from_value: current_num,
      to_value: target_value,
      duration_ms: duration_ms,
      suffix: "%",
      decimal_places: 1,
      easing: Easing.EaseOutQuad
    } );
  }

  private extractNumberFromText(): number | null
  {
    const matches = this.text.match( /[-+]?(\d+\.?\d*|\.\d+)/ );
    return matches ? parseFloat( matches[0] ) : null;
  }

  private drawArcText()
  {
    const txt = this.text;
    const chars = txt.split( '' );

    this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ this.font_size }px ${ this.text_style }`;
    this.ctx.textBaseline = "middle";
    this.ctx.textAlign = "center";
    this.ctx.fillStyle = this.text_color;

    const widths = chars.map( ch =>
    {
      this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ this.font_size }px ${ this.text_style }`;
      return this.ctx.measureText( ch ).width;
    } );
    const text_width = widths.reduce( ( sum, w ) => sum + w, 0 )
      + Math.max( 0, chars.length - 1 ) * this.letter_spacing;

    const r = this.arc_radius!;
    const total_angle = text_width / r;
    const start_angle = -total_angle / 2;

    let angle = start_angle;
    for ( let i = 0; i < chars.length; i++ )
    {
      const w = widths[i];
      const char_angle = w / r;

      this.ctx.save();
      const center_angle = angle + char_angle / 2;
      this.ctx.translate(
        r * Math.sin( center_angle ),
        r * ( 1 - Math.cos( center_angle ) )
      );
      this.ctx.rotate( center_angle );

      this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ this.font_size }px ${ this.text_style }`;

      if ( this.shadow_blur > 0 )
      {
        this.ctx.shadowBlur = this.shadow_blur;
        this.ctx.shadowColor = this.shadow_color;
        this.ctx.shadowOffsetX = this.shadow_offset_x;
        this.ctx.shadowOffsetY = this.shadow_offset_y;
      } else
      {
        this.ctx.shadowBlur = 0;
      }

      if ( this.outline_width > 0 )
      {
        this.ctx.lineWidth = this.outline_width;
        this.ctx.strokeStyle = this.outline_color;
        const prev_shadow_blur = this.ctx.shadowBlur;
        this.ctx.shadowBlur = 0;
        this.ctx.strokeText( chars[i], 0, 0 );
        this.ctx.shadowBlur = prev_shadow_blur;
      }

      this.ctx.fillStyle = this.text_color;
      this.ctx.fillText( chars[i], 0, 0 );

      this.ctx.restore();
      angle += char_angle;
      if ( i < chars.length - 1 )
        angle += this.letter_spacing / r;
    }
  }

  private static readonly SCALE_TO_WIDTH_TOLERANCE: number = 1.03;

  private scaleTextToWidth(): void
  {
    const available_width = this.rect.getW();
    const max_allowed_width = available_width * UITextElement.SCALE_TO_WIDTH_TOLERANCE;
    const min_font_size = this.min_font_size;

    this.font_size = this.base_font_size;
    if ( this.getNaturalTextWidth() <= max_allowed_width )
    {
      return;
    }

    this.font_size = min_font_size;
    if ( this.getNaturalTextWidth() > max_allowed_width )
    {
      return;
    }

    let left = min_font_size;
    let right = this.base_font_size;
    let best_font_size = min_font_size;

    while ( left <= right )
    {
      const mid = Math.floor( ( left + right ) / 2 );
      this.font_size = mid;

      if ( this.getNaturalTextWidth() <= max_allowed_width )
      {
        best_font_size = mid;
        left = mid + 1;
      }
      else
      {
        right = mid - 1;
      }
    }

    this.font_size = best_font_size;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    if ( this.scale_to_width && !this.natural_text_width && !this.auto_wrap )
    {
      this.scaleTextToWidth();
    }

    if ( this.natural_text_width )
    {
      this.rect.resize( this.getNaturalTextWidth(), this.rect.getH() );
    }

    this.startRender();

    if ( this.arc_radius != null )
    {
      this.drawArcText();
      this.finishRender();
      return;
    }

    if ( this.show_dbg_zone ) this.drawRectAreaForTest();

    const h = this.rect.getH();
    const w = this.rect.getW();
    const w2 = this.rect.getW2();

    const padding_x = Math.max( this.outline_width + Math.abs( this.outline_offset_x ), this.shadow_blur + Math.abs( this.shadow_offset_x ) );
    const padding_y = Math.max( this.outline_width + Math.abs( this.outline_offset_y ), this.shadow_blur + Math.abs( this.shadow_offset_y ) );

    this.ctx.beginPath();
    this.ctx.rect( -w2 - padding_x, -h / 2 - padding_y, w + padding_x * 2, h + padding_y * 2 );
    this.ctx.clip();

    // let lines = this.text.split("\n");

    // // wrap each line if too long
    // const wrapped: string[] = [];
    // for ( const line of lines )
    // {
    //   if ( this.ctx.measureText( line ).width > w )
    //   {
    //     const sublines = this.wrapTextToLines( line, w, this.font_size );
    //     wrapped.push( ...sublines );
    //   } else
    //   {
    //     wrapped.push( line );
    //   }
    // }
    // lines = wrapped;
    let lines = this.text.split( "\n" );

    if ( this.auto_wrap )
    {
      this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ this.font_size }px ${ this.text_style }`;
      const wrapped: string[] = [];
      for ( const line of lines )
      {
        if ( this.ctx.measureText( line ).width > w )
        {
          const sublines = this.wrapTextToLines( line, w, this.font_size );
          wrapped.push( ...sublines );
        } else
        {
          wrapped.push( line );
        }
      }
      lines = wrapped;
    }


    let font_size = this.font_size;
    const line_height_multiplier = this.line_height ?? 1.1;
    if ( this.fit_font_size )
    {
      font_size = this.getFittedFontSize( lines, w, h, font_size, line_height_multiplier );
    }

    if ( lines.length > 1 ) 
    {
      const max_font_size = h / (lines.length * line_height_multiplier);
      font_size = Math.min( font_size, max_font_size );
    }

    this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ font_size }px ${ this.text_style }`;
    this.ctx.textBaseline = "middle";
    
    const line_height = font_size * line_height_multiplier;
    const total_text_height = lines.length * line_height;

    let start_y: number;
    switch ( this.vertical_align ) 
    {
      case MyTextVerticalAlign.Top:
        start_y = -h / 2 + line_height / 2 + this.padding_y;
        break;
      case MyTextVerticalAlign.Bottom:
        start_y =  h / 2 - total_text_height + line_height / 2 + this.padding_y;
        break;
      case MyTextVerticalAlign.Center:
      default:
        start_y = -total_text_height / 2 + line_height / 2 + this.padding_y;
        break;
    }

    const shared_offset_y = lines.length > 1 ? this.measureBlockOffsetY( lines, font_size ) : null;

    for ( let i = 0; i < lines.length; i++ )
    {
      const line_y = start_y + i * line_height;
      const segments = this.parseSegments( lines[i] );
      const line_offset_y = shared_offset_y ?? ( segments.length > 1 ? this.measureBlockOffsetY( [lines[i]], font_size ) : null );
      const seg_widths = segments.map( seg =>
      {
        this.ctx.font = `${ this.font_style } ${ seg.weight } ${ font_size }px ${ this.text_style }`;
        return this.ctx.measureText(seg.text).width;
      });
      const unscaled_total_width = seg_widths.reduce( ( sum, w ) => sum + w, 0) ;
      const scale_x = Math.min( 1, w / unscaled_total_width );

      // let base_x: number;
      // switch (this.text_align) {
      //   case MyTextAlign.Left:
      //     base_x = -w2;
      //     break;
      //   case MyTextAlign.Right:
      //     base_x = w2 - unscaled_total_width;
      //     break;
      //   default:
      //     base_x = -unscaled_total_width / 2;
      // }

      let base_x: number;
      switch ( this.text_align )
      {
        case MyTextAlign.Left:
          base_x = -w2 / scale_x;
          break;
        case MyTextAlign.Right:
          base_x = ( w2 - unscaled_total_width ) / scale_x;
          break;
        default:
          base_x = -unscaled_total_width / 2;
      }

      this.ctx.save();
      this.ctx.translate( 0, line_y );
      this.ctx.scale( scale_x, 1 );
      this.ctx.textAlign = "left";

      if ( this.shadow_blur > 0 )
      {
        this.ctx.shadowBlur = this.shadow_blur;
        this.ctx.shadowColor = this.shadow_color;
        this.ctx.shadowOffsetX = this.shadow_offset_x;
        this.ctx.shadowOffsetY = this.shadow_offset_y;
      }

      let cursor_x = base_x;
      for ( const seg of segments ) 
      {
        this.ctx.font = `${ this.font_style } ${ seg.weight } ${ font_size }px ${ this.text_style }`;

        const metrics = this.ctx.measureText( seg.text );
        const offset_y = this.using_y_offset
          ? line_offset_y ?? ( metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent ) / 2
          : 0;

        if ( this.outline_width > 0 )
        {
          this.ctx.lineWidth = this.outline_width;
          this.ctx.strokeStyle = this.outline_gradient_color
            ? this.createTextGradient( cursor_x, offset_y, metrics.width, font_size, this.outline_gradient_color )
            : this.outline_color;
          this.ctx.shadowBlur = this.outline_shadow_blur;
          this.ctx.shadowColor = this.outline_shadow_color;
          this.ctx.shadowOffsetX = this.outline_shadow_offset_x;
          this.ctx.shadowOffsetY = this.outline_shadow_offset_y;
          this.ctx.lineJoin = "round";
          this.ctx.lineCap = "round";
          this.ctx.miterLimit = 2;
          this.ctx.strokeText(seg.text, cursor_x + this.outline_offset_x, offset_y + this.outline_offset_y);
          this.ctx.shadowBlur = this.shadow_blur;
          this.ctx.shadowColor = this.shadow_color;
          this.ctx.shadowOffsetX = this.shadow_offset_x;
          this.ctx.shadowOffsetY = this.shadow_offset_y;
        }
        if ( this.gradient_text_color )
        {
          this.ctx.fillStyle = this.createTextGradient(
            cursor_x,
            offset_y,
            metrics.width,
            font_size
          );
        } else
        {
          this.ctx.fillStyle = seg.color;
        }

        this.ctx.fillText( seg.text, cursor_x, offset_y );
        cursor_x += this.ctx.measureText( seg.text ).width;
      }
      this.ctx.restore();
    }

    this.finishRender();
  }

  private measureBlockOffsetY( lines: string[], font_size: number ): number
  {
    const prev_font = this.ctx.font;
    this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ font_size }px ${ this.text_style }`;

    const metrics = this.ctx.measureText( lines.map( line => this.parseSegments( line ).map( seg => seg.text ).join( "" ) ).join( "" ) );
    const offset_y = ( metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent ) / 2;

    this.ctx.font = prev_font;

    return offset_y;
  }

  private getFittedFontSize( lines: string[], width: number, height: number, font_size: number, line_height_multiplier: number ): number
  {
    let max_width = 0;

    for ( const line of lines )
    {
      const segments = this.parseSegments( line );
      let line_width = 0;

      for ( const seg of segments )
      {
        this.ctx.font = `${ this.font_style } ${ seg.weight } ${ font_size }px ${ this.text_style }`;
        line_width += this.ctx.measureText( seg.text ).width;
      }

      max_width = Math.max( max_width, line_width );
    }

    const width_scale = max_width > 0 ? Math.min( 1, width / max_width ) : 1;
    const height_scale = lines.length > 0
      ? Math.min( 1, height / ( lines.length * font_size * line_height_multiplier ) )
      : 1;

    return Math.max( this.min_font_size, font_size * Math.min( width_scale, height_scale ) );
  }

  protected parseSegments( text: string ): { text: string; color: string; weight: number }[]
  {
    const segs = [];
    let remaining = text;
    const tag_regex = /<([a-zA-Z0-9-]+)>(.*?)<\/\1>/s;
    while ( true )
    {
      const match = tag_regex.exec( remaining );
      if ( !match ) break;
      const [full, tag, content] = match;
      const idx = match.index;
      if ( idx > 0 ) segs.push( { text: remaining.slice( 0, idx ), color: this.text_color, weight: this.font_weight } );
      const parsed = this.parseComplexTag( tag );
      const color = parsed.color || this.color_map[tag] || this.text_color;
      const weight = parsed.weight || this.font_weight;
      segs.push( { text: content, color, weight } );
      remaining = remaining.slice( idx + full.length );
    }
    if ( remaining.length > 0 ) segs.push( { text: remaining, color: this.text_color, weight: this.font_weight } );
    return segs;
  }

  protected parseComplexTag( tag: string ): { color?: string; weight?: number }
  {
    const result: { color?: string; weight?: number } = {};
    const parts = tag.split( '-' );
    for ( const part of parts )
    {
      if ( this.color_map[part] ) result.color = this.color_map[part];
      if ( this.weight_map[part] ) result.weight = this.weight_map[part];
      const weight_match = part.match( /^weight(\d+)$/ );
      if ( weight_match )
      {
        const w = parseInt( weight_match[1], 10 );
        if ( w >= 100 && w <= 900 ) result.weight = w;
      }
      const num = parseInt( part, 10 );
      if ( !isNaN( num ) && num >= 100 && num <= 900 ) result.weight = num;
    }
    return result;
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
  }

  public getRect(): Rect
  {
    return this.rect;
  }

  private wrapTextToLines( text: string, maxWidth: number, fontSize: number ): string[]
  {
    const lines: string[] = [];
    this.ctx.font = `${ this.font_style } ${ this.font_weight } ${ fontSize }px ${ this.text_style }`;
    const strip = ( s: string ) => s.replace( /<[^>]+>/g, '' );

    // якщо є пробіли — класична логіка
    if ( text.includes(" ") )
    {
      const words = text.split(" ");
      let line = "";
      for ( let i = 0; i < words.length; i++ )
      {
        const testLine = line + words[i] + " ";
        const testWidth = this.ctx.measureText( strip( testLine ) ).width;
        if ( testWidth > maxWidth && line !== "" )
        {
          lines.push( line.trim() );
          line = words[i] + " ";
        } else
        {
          line = testLine;
        }
      }
      if ( line ) lines.push( line.trim() );
    } else
    {
      // слова без пробілів — переносимо по символах
      let line = "";
      for ( let i = 0; i < text.length; i++ )
      {
        const testLine = line + text[i];
        const testWidth = this.ctx.measureText( strip( testLine ) ).width;
        if (testWidth > maxWidth && line !== "")
        {
          lines.push(line);
          line = text[i];
        } else {
          line = testLine;
        }
      }
      if ( line ) lines.push( line );
    }

    return this.repairSplitTags( lines );
  }

  private repairSplitTags( lines: string[] ): string[]
  {
    let carry = '';
    return lines.map( line =>
    {
      const prefixed = carry + line;
      const opens  = ( prefixed.match( /<([a-zA-Z0-9-]+)>/g ) ?? [] ).map( t => t.slice( 1, -1 ) );
      const closes = ( prefixed.match( /<\/([a-zA-Z0-9-]+)>/g ) ?? [] ).map( t => t.slice( 2, -1 ) );
      const unclosed = opens.filter( ( t, i ) => closes[i] !== t );
      if ( unclosed.length > 0 )
      {
        const tag = unclosed[unclosed.length - 1];
        carry = `<${tag}>`;
        return prefixed + `</${tag}>`;
      }
      carry = '';
      return prefixed;
    } );
  }
}
